import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../lib/apiError';
import { rolesApi } from './rolesApi';
import type { RoleDetail } from './roleTypes';

export interface RoleProfileState {
  role: RoleDetail | null;
  loading: boolean;
  error: ApiError | null;
  reload: () => void;
}

/**
 * One role, by id — the same shape as `useUserProfile`, deliberately.
 *
 * A superseded request is aborted rather than allowed to land, so navigating between two
 * roles cannot leave the slower response painting over the newer one.
 */
export function useRoleProfile(id: string | undefined): RoleProfileState {
  const [role, setRole] = useState<RoleDetail | null>(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    A different role drops what is on screen, in its own effect. A RELOAD of the same role keeps
    it, so a save refreshes the page under the header instead of skeletoning all of it.
  */
  useEffect(() => {
    setRole(null);
  }, [id]);

  useEffect(() => {
    if (!id) {
      setError(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    rolesApi
      .get(id, controller.signal)
      .then(setRole)
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(caught instanceof ApiError ? caught : new ApiError('Could not load the role.', 0));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [id, reloadKey]);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return { role, loading, error, reload };
}
